import { createFileRoute } from '@tanstack/react-router';
import {
  Avatar, Button, Card, FileButton, Group, Loader, Select, Stack, Text, Textarea, TextInput, Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import * as userApi from '@/features/users/api';
import { useDirtyFormGuard } from '@/shared/hooks/useDirtyFormGuard';

export const Route = createFileRoute('/_app/settings/profile')({ component: ProfilePage });

const PHONE_RE = /^\+?[\d\s-]{5,20}$/;
const PHONE_MSG = 'Solo cifre, +, spazi e trattini';

/** Campo opzionale: vuoto OK, ma se compilato deve rispettare lo schema. */
const optional = (schema: z.ZodTypeAny) => schema.or(z.literal(''));

const accountSchema = z.object({
  email: z.string().email('Email non valida'),
  phoneNumber: optional(z.string().regex(PHONE_RE, PHONE_MSG)),
});

const profileSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  nickName: z.string(),
  sex: z.string(),
  taxId: optional(z.string().regex(/^[A-Za-z0-9]{11,16}$/, 'Codice fiscale non valido')),
  birthDate: z.string().refine((v) => !v || new Date(v) <= new Date(), 'La data non può essere nel futuro'),
  fixedPhone: optional(z.string().regex(PHONE_RE, PHONE_MSG)),
  mobilePhone: optional(z.string().regex(PHONE_RE, PHONE_MSG)),
  contactEmail: optional(z.string().email('Email non valida')),
  residenceAddress: z.string(),
  residenceHouseNumber: z.string(),
  residenceCity: z.string(),
  residenceProvince: optional(z.string().max(50, 'Massimo 50 caratteri')),
  residenceZIP: optional(z.string().regex(/^\d{4,10}$/, 'CAP non valido (solo cifre)')),
  residenceState: z.string(),
  occupation: z.string(),
  description: z.string(),
  userLang: z.string(),
});

function ProfilePage() {
  const { decoded, user } = useAuth();
  const userId = decoded?.sub ?? user?.id ?? '';
  const qc = useQueryClient();
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['me-with-profile', userId],
    queryFn: () => userApi.fetchUserWithProfile(userId),
    enabled: Boolean(userId),
  });
  const me = data?.user;
  const profile = data?.profile;

  const accountForm = useForm({
    mode: 'controlled',
    initialValues: { email: '', phoneNumber: '' },
    validate: zodResolver(accountSchema),
  });

  const profileForm = useForm({
    mode: 'controlled',
    initialValues: {
      firstName: '', lastName: '', nickName: '',
      sex: '', taxId: '', birthDate: '',
      fixedPhone: '', mobilePhone: '', contactEmail: '',
      residenceAddress: '', residenceHouseNumber: '', residenceCity: '',
      residenceProvince: '', residenceZIP: '', residenceState: '',
      occupation: '', description: '', userLang: 'it',
    },
    validate: zodResolver(profileSchema),
  });

  // Avvisa su chiusura tab / navigazione con modifiche non salvate
  useDirtyFormGuard(accountForm.isDirty() || profileForm.isDirty());

  useEffect(() => {
    if (me) {
      accountForm.setValues({ email: me.email ?? '', phoneNumber: me.phoneNumber ?? '' });
      accountForm.resetDirty({ email: me.email ?? '', phoneNumber: me.phoneNumber ?? '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  useEffect(() => {
    if (profile) {
      const values = {
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        nickName: profile.nickName ?? '',
        sex: profile.sex ?? '',
        taxId: profile.taxId ?? '',
        birthDate: profile.birthDate ? profile.birthDate.slice(0, 10) : '',
        fixedPhone: profile.fixedPhone ?? '',
        mobilePhone: profile.mobilePhone ?? '',
        contactEmail: profile.contactEmail ?? '',
        residenceAddress: profile.residenceAddress ?? '',
        residenceHouseNumber: profile.residenceHouseNumber ?? '',
        residenceCity: profile.residenceCity ?? '',
        residenceProvince: profile.residenceProvince ?? '',
        residenceZIP: profile.residenceZIP ?? '',
        residenceState: profile.residenceState ?? '',
        occupation: profile.occupation ?? '',
        description: profile.description ?? '',
        userLang: profile.userLang ?? 'it',
      };
      profileForm.setValues(values);
      profileForm.resetDirty(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const saveAccount = useMutation({
    mutationFn: (v: typeof accountForm.values) => userApi.updateMe(userId, v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Account aggiornato' });
      accountForm.resetDirty();
      qc.invalidateQueries({ queryKey: ['me-with-profile', userId] });
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore account' }),
  });

  const saveProfile = useMutation({
    mutationFn: (v: typeof profileForm.values) => {
      const attrs = { ...v, birthDate: v.birthDate || undefined };
      if (profile?.id) return userApi.updateUserProfile(profile.id, attrs);
      return userApi.createUserProfile(userId, { ...attrs, contactEmail: attrs.contactEmail || me?.email });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Profilo aggiornato' });
      profileForm.resetDirty();
      qc.invalidateQueries({ queryKey: ['me-with-profile', userId] });
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore profilo' }),
  });

  const handleAvatar = async (file: File | null) => {
    if (!file) return;
    setUploadingAvatar(true);
    try {
      await userApi.uploadAvatar(file);
      notifications.show({ color: 'green', message: 'Avatar aggiornato' });
      qc.invalidateQueries({ queryKey: ['me-with-profile', userId] });
    } catch {
      notifications.show({ color: 'red', message: 'Upload fallito' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <Stack>
      <Title order={2}>Profilo</Title>

      <Card withBorder padding="lg" radius="md">
        <Group>
          <Avatar size={80} radius="xl">
            {(profile?.firstName?.[0] ?? me?.firstName?.[0] ?? me?.email?.[0] ?? 'U').toUpperCase()}
          </Avatar>
          <FileButton onChange={handleAvatar} accept="image/png,image/jpeg,image/webp">
            {(props) => (
              <Button variant="light" loading={uploadingAvatar} {...props}>
                Cambia foto
              </Button>
            )}
          </FileButton>
        </Group>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Title order={4} mb="sm">Account</Title>
        <form onSubmit={accountForm.onSubmit((v) => void saveAccount.mutate(v))}>
          <Stack>
            <TextInput label="Email" disabled {...accountForm.getInputProps('email')} />
            <TextInput label="Telefono account" {...accountForm.getInputProps('phoneNumber')} />
            <Group justify="flex-end">
              <Button type="submit" variant="light" loading={saveAccount.isPending}>Salva account</Button>
            </Group>
          </Stack>
        </form>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Title order={4} mb="sm">Anagrafica</Title>
        {!profile && (
          <Text size="sm" c="orange" mb="sm">Il tuo profilo non e&apos; ancora stato creato: compila e salva.</Text>
        )}
        <form onSubmit={profileForm.onSubmit((v) => void saveProfile.mutate(v))}>
          <Stack>
            <Group grow>
              <TextInput label="Nome" {...profileForm.getInputProps('firstName')} />
              <TextInput label="Cognome" {...profileForm.getInputProps('lastName')} />
            </Group>
            <Group grow>
              <TextInput label="Nickname" {...profileForm.getInputProps('nickName')} />
              <Select
                label="Sesso"
                data={[{ value: 'M', label: 'M' }, { value: 'F', label: 'F' }, { value: 'X', label: 'Altro' }]}
                clearable
                {...profileForm.getInputProps('sex')}
              />
            </Group>
            <Group grow>
              <TextInput label="Codice fiscale" {...profileForm.getInputProps('taxId')} />
              <TextInput label="Data di nascita" type="date" {...profileForm.getInputProps('birthDate')} />
            </Group>
            <Group grow>
              <TextInput label="Telefono fisso" {...profileForm.getInputProps('fixedPhone')} />
              <TextInput label="Cellulare" {...profileForm.getInputProps('mobilePhone')} />
            </Group>
            <TextInput label="Email di contatto" {...profileForm.getInputProps('contactEmail')} />

            <Title order={5} mt="sm">Residenza</Title>
            <Group grow>
              <TextInput label="Indirizzo" {...profileForm.getInputProps('residenceAddress')} />
              <TextInput label="Civico" maw={120} {...profileForm.getInputProps('residenceHouseNumber')} />
            </Group>
            <Group grow>
              <TextInput label="Citta'" {...profileForm.getInputProps('residenceCity')} />
              <TextInput label="Provincia" {...profileForm.getInputProps('residenceProvince')} />
              <TextInput label="CAP" maw={140} {...profileForm.getInputProps('residenceZIP')} />
            </Group>
            <TextInput label="Stato" {...profileForm.getInputProps('residenceState')} />

            <Title order={5} mt="sm">Altro</Title>
            <Group grow>
              <TextInput label="Occupazione" {...profileForm.getInputProps('occupation')} />
              <Select
                label="Lingua"
                data={[{ value: 'it', label: 'Italiano' }, { value: 'en', label: 'English' }]}
                {...profileForm.getInputProps('userLang')}
              />
            </Group>
            <Textarea label="Note" autosize minRows={2} {...profileForm.getInputProps('description')} />

            <Group justify="flex-end">
              <Button type="submit" loading={saveProfile.isPending}>
                {profile ? 'Salva profilo' : 'Crea profilo'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}
